<template>
	<div class="main-container">
		<div class="container">
			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-2">
					<h2>Admin Utilities</h2>
					<div class="alert alert-success" role="alert" v-if="updateMessage">
						{{ updateMessage }}
					</div>
				</div>
			</div>

			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-4">
					<form @submit.prevent="onFundsFormSubmit" class="border p-3">
						<div class="row mb-2">
							<h3>Import Fund Details</h3>
							<div class="form-text">Import the Fund size, amount invested, amount committed spreadsheet.</div>
						</div>
						<div class="row">
							<label class="col-sm-2">
								<button class="btn btn-primary btn-block">Upload</button>
							</label>
							<div class="col-sm-10">
								<input type="file" class="form-control" @change="onFundsFileChanged">
								<span class="field-error" v-if="v$.selectedFundsFile.$error">{{
										v$.selectedFundsFile.$errors[0].$message
									}}</span>
							</div>
						</div>
					</form>
				</div>
			</div>

			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-4">
					<form @submit.prevent="onFinancialsFormSubmit" class="border p-3">
						<div class="row mb-2">
							<h3>Import Investment Financials</h3>
							<div class="form-text">Import the Fund size, amount invested, amount committed spreadsheet.</div>
						</div>
						<div class="row mb-3">
							<label class="col-sm-2 col-form-label">Company *</label>
							<div class="col-sm-10">
								<select v-model="company" class="form-select">
									<option value="" disabled>Select</option>
									<option v-for="company in companies" :key="company" :value="company.id">{{ company.name }}</option>
								</select>
								<span class="field-error" v-if="v$.company.$error">{{ v$.company.$errors[0].$message }}</span>
							</div>
						</div>

						<div class="row mb-3">
							<label class="col-sm-2 col-form-label">File *</label>
							<div class="col-sm-10">
								<input type="file" class="form-control" @change="onFinancialsFileChanged">
								<span class="field-error" v-if="v$.selectedFinancialsFile.$error">{{ v$.selectedFinancialsFile.$errors[0].$message }}</span>
							</div>
						</div>
						<div>
							<button class="btn btn-primary btn-block">Upload</button>
						</div>
					</form>

				</div>
			</div>

			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-4">
					<form @submit.prevent="onCashFormSubmit" class="border p-3">
						<div class="row mb-2">
							<h3>Import Cash In/Cash Out</h3>
							<div class="form-text">Import the cash in/cash out spreadsheet.</div>
						</div>
						<div class="row">
							<label class="col-sm-2">
								<button class="btn btn-primary btn-block">Upload</button>
							</label>
							<div class="col-sm-10">
								<input type="file" class="form-control" @change="onCashFileChanged">
								<span class="field-error" v-if="v$.selectedCashFile.$error">{{
										v$.selectedCashFile.$errors[0].$message
									}}</span>
							</div>
						</div>
					</form>
				</div>
			</div>

		</div>

			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-4">
					<div class="border p-3">
						<div class="row mb-2">
							<h3>Install EAV Manifests</h3>
							<div class="form-text">
								Installs EAV type definitions (object types and fields) for all tenants.
								Run once after initial data migration, before reprocessing financials.
								Safe to run multiple times — idempotent.
							</div>
						</div>
						<div class="row mb-3" v-if="manifestsMessage">
							<div class="col">
								<div :class="manifestsError ? 'alert alert-danger' : 'alert alert-success'">
									{{ manifestsMessage }}
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-auto">
								<button class="btn btn-warning" @click="onBootstrapManifests" :disabled="manifestsLoading">
									{{ manifestsLoading ? 'Installing...' : 'Install EAV Manifests' }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-4">
					<div class="border p-3">
						<div class="row mb-2">
							<h3>Reprocess Migrated Financials</h3>
							<div class="form-text">
								Regenerates EAV financial data records from all previously approved uploads.
								Run once after initial data migration. Preserves original approver and approval dates.
								Fund-level data is processed before investment-level data.
							</div>
						</div>
						<div class="row mb-3" v-if="reprocessMessage">
							<div class="col">
								<div :class="reprocessError ? 'alert alert-danger' : 'alert alert-info'">
									{{ reprocessMessage }}
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-auto">
								<button class="btn btn-warning" @click="onReprocessFinancials">
									Reprocess All Financial Data
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="row">
				<div class="col-md-7 ml-sm-auto col-lg-7 px-4 mb-4">
					<div class="border p-3">
						<div class="row mb-2">
							<h3>Reprocess Investment Upload</h3>
							<div class="form-text">
								Re-parses the latest completed upload for a specific investment and regenerates its EAV financial data records.
							</div>
						</div>
						<div class="row mb-3">
							<label class="col-sm-2 col-form-label">Company *</label>
							<div class="col-sm-10">
								<select v-model="reprocessInvestmentId" class="form-select">
									<option value="" disabled>Select</option>
									<option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
								</select>
							</div>
						</div>
						<div class="row mb-3" v-if="reprocessInvestmentMessage">
							<div class="col">
								<div :class="reprocessInvestmentError ? 'alert alert-danger' : 'alert alert-info'">
									{{ reprocessInvestmentMessage }}
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-auto">
								<button class="btn btn-warning" @click="onReprocessInvestment" :disabled="!reprocessInvestmentId">
									Reprocess Upload
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

		</div>

</template>

<script>

import useVuelidate from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import DateUtils from "@/mixins/DateUtils";
import {InvestmentsApi} from "@/api/investment_api";
import {MigrationApi} from "@/api/migration_api";
import {UploadsApi} from "@/api/uploads_api";

export default {
	name: 'AdminView',
	components: {},
	mixins: [DateUtils],
	data() {
		return {
			updateMessage: '',
			company: '',
			companies: [],
			selectedFundsFile: null,
			selectedFinancialsFile: null,
			selectedCashFile: null,
			manifestsMessage: '',
			manifestsError: false,
			manifestsLoading: false,
			reprocessMessage: '',
			reprocessError: false,
			reprocessInvestmentId: '',
			reprocessInvestmentMessage: '',
			reprocessInvestmentError: false,
		}
	},
	methods: {
		onFundsFileChanged(event) {
			this.selectedFundsFile = event.target.files[0]
		},
		async onFundsFormSubmit() {
			this.updateMessage = ""
			this.v$.selectedFundsFile.$validate()
			if (!this.v$.selectedFundsFile.$error) {
				const upload = await UploadsApi.newFinancialsUpload({
					file: this.selectedFundsFile,
					uploadType: 'fund_data'
				})
				await UploadsApi.updateUploadStatus(upload.guid, 'APPROVED')
				this.updateMessage = "Successfully imported Fund details"
			}
		},

		onFinancialsFileChanged(event) {
			this.selectedFinancialsFile = event.target.files[0]
		},
		async onFinancialsFormSubmit() {
			this.updateMessage = ""
			this.v$.selectedFinancialsFile.$validate()
			if (!this.v$.selectedFinancialsFile.$error) {
				const upload = await UploadsApi.newFinancialsUpload({
					file: this.selectedFinancialsFile,
					uploadType: 'financials',
					investmentId: this.company
				})
				// Immediately approve it
				await UploadsApi.updateUploadStatus(upload.guid, 'APPROVED')
				this.updateMessage = "Successfully imported financials data"
			}
		},

		onCashFileChanged(event) {
			this.selectedCashFile = event.target.files[0]
		},
		async onCashFormSubmit() {
			this.updateMessage = ""
			this.v$.selectedCashFile.$validate()
			if (!this.v$.selectedCashFile.$error) {
				const upload = await UploadsApi.newFinancialsUpload({
					file: this.selectedCashFile,
					uploadType: 'cash_flow'
				})
				await UploadsApi.updateUploadStatus(upload.guid, 'APPROVED')
				this.updateMessage = "Successfully imported cash flow data"
			}
		},

		async onBootstrapManifests() {
			if (!confirm('Install EAV manifests for all tenants? This is safe to run multiple times.')) return
			this.manifestsMessage = ''
			this.manifestsError = false
			this.manifestsLoading = true
			try {
				await MigrationApi.bootstrapManifests()
				this.manifestsMessage = 'EAV manifests installed successfully.'
			} catch (e) {
				this.manifestsError = true
				this.manifestsMessage = `Failed to install manifests: ${e.message || 'Unknown error'}`
			} finally {
				this.manifestsLoading = false
			}
		},

		async onReprocessFinancials() {
			if (!confirm('This will regenerate all EAV financial data records from approved uploads. The job runs asynchronously on the server — progress is visible in server logs. Continue?')) {
				return
			}
			this.reprocessMessage = ''
			this.reprocessError = false
			try {
				await MigrationApi.reprocessFinancials()
				this.reprocessMessage = 'Reprocess job started. Running in the background — check server logs for completion.'
			} catch (e) {
				this.reprocessError = true
				this.reprocessMessage = `Failed to start reprocess job: ${e.message || 'Unknown error'}`
			}
		},

		async onReprocessInvestment() {
			if (!this.reprocessInvestmentId) return
			const name = this.companies.find(c => c.id === this.reprocessInvestmentId)?.name || 'this investment'
			if (!confirm(`Reprocess the latest upload for ${name}? The job runs asynchronously on the server.`)) return
			this.reprocessInvestmentMessage = ''
			this.reprocessInvestmentError = false
			try {
				await MigrationApi.reprocessInvestment(this.reprocessInvestmentId)
				this.reprocessInvestmentMessage = `Reprocess job started for ${name}. Running in the background — check server logs for completion.`
			} catch (e) {
				this.reprocessInvestmentError = true
				this.reprocessInvestmentMessage = `Failed to start reprocess job: ${e.message || 'Unknown error'}`
			}
		},
	},
	setup() {
		return {v$: useVuelidate()}
	},
	validations() {
		return {
			selectedFundsFile: {required},
			selectedFinancialsFile: {required},
			company: {required},
			selectedCashFile: {required},
		}
	},
	mounted() {
		InvestmentsApi.getInvestments({ '$orderby': 'name asc', '$top': 1000 })
			.then(response => {
				this.companies = (response.value || response).map(inv => ({ id: inv.guid, name: inv.name }))
			})
	}
}
</script>
